import { Flame, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface LaneItem {
  shortName: string;
  rate: number;
  rateDisplay: string;
  volume: number;
  originCity?: string;
  originState?: string;
  destCity?: string;
  destState?: string;
}

interface HotLanesPanelProps {
  lanes: LaneItem[];
  equipment: string;
  isLoading?: boolean;
}

export function HotLanesPanel({ lanes, equipment, isLoading }: HotLanesPanelProps) {
  const navigate = useNavigate();
  // Top by volume (hot = where the freight is)
  const top = [...lanes].sort((a, b) => b.volume - a.volume).slice(0, 3);

  const goToSearch = (l: LaneItem) => {
    const params = new URLSearchParams();
    if (l.originCity && l.originState) params.set("origin", `${l.originCity}, ${l.originState}`);
    if (l.destCity && l.destState) params.set("destination", `${l.destCity}, ${l.destState}`);
    if (equipment) params.set("equipment", equipment);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div
      className="flex flex-col h-full rounded-lg border bg-card overflow-hidden"
      style={{ boxShadow: "var(--shadow-sm)" }}
    >
      <div className="flex items-center justify-between border-b px-3 py-2.5 bg-muted/20">
        <div className="flex items-center gap-1.5">
          <Flame className="h-3.5 w-3.5 text-accent" />
          <h4 className="text-caption font-bold text-foreground">Hot Lanes · {equipment}</h4>
        </div>
        <span className="text-[10px] text-muted-foreground">By volume</span>
      </div>

      <div className="flex-1 flex flex-col">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : top.length === 0 ? (
          <div className="flex-1 flex items-center justify-center px-3 py-6 text-center">
            <p className="text-[11px] text-muted-foreground leading-snug">
              No active lanes for {equipment} yet.<br />Check back as the board fills in.
            </p>
          </div>
        ) : (
          <div className="flex-1 divide-y">
            {top.map((l, i) => (
              <button
                key={i}
                onClick={() => goToSearch(l)}
                className="w-full flex items-center justify-between px-3 py-2 gap-2 min-w-0 text-left hover:bg-muted/40 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold text-foreground truncate">{l.shortName}</p>
                  <p className="text-[10px] text-muted-foreground">{l.volume} active load{l.volume !== 1 ? "s" : ""}</p>
                </div>
                <span className="text-[11px] font-bold text-foreground shrink-0">{l.rateDisplay}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
